import { useEffect, useMemo, useState } from 'react'
import type { CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import '../../styles/halloween-pumpkins.css'

type PumpkinStyle = CSSProperties & {
  '--x': string
  '--y': string
  '--size': string
  '--duration': string
  '--delay': string
  '--travel': string
  '--opacity': string
}

function random(seed: number): number {
  const n = Math.sin(seed * 127.1 + 311.7) * 43758.5453
  return n - Math.floor(n)
}

function PumpkinIcon({ variant }: { variant: number }) {
  const gradientId = `kr-header-pumpkin-gradient-${variant}`
  return (
    <svg viewBox="0 0 64 64" width="100%" height="100%" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ffc36b" />
          <stop offset="0.55" stopColor="#ef852e" />
          <stop offset="1" stopColor="#b94d1c" />
        </linearGradient>
      </defs>
      <path d="M29 18 Q27 9 34 6 Q32 13 36 19" fill="none" stroke="#719257" strokeWidth="5" strokeLinecap="round" />
      <ellipse cx="32" cy="39" rx="25" ry="21" fill={`url(#${gradientId})`} stroke="#813a1b" strokeWidth="1.5" />
      <path d="M32 18 C15 22 15 57 32 60 M32 18 C49 22 49 57 32 60" fill="none" stroke="#a74c20" strokeOpacity=".45" strokeWidth="2" />
      {variant === 0 ? (
        <>
          <path d="M17 34 L27 30 L24 40 Z M47 34 L37 30 L40 40 Z" fill="#39211c" />
          <path d="M19 46 Q32 57 45 46 L39 48 L35 45 L31 50 L26 45 Z" fill="#39211c" />
        </>
      ) : variant === 1 ? (
        <>
          <path d="M18 36 L25 29 L29 38 Z M46 36 L39 29 L35 38 Z" fill="#39211c" />
          <path d="M20 47 L26 44 L31 49 L36 44 L44 47 Q32 56 20 47" fill="#39211c" />
        </>
      ) : (
        <>
          <ellipse cx="23" cy="36" rx="4" ry="5" fill="#39211c" />
          <ellipse cx="41" cy="36" rx="4" ry="5" fill="#39211c" />
          <path d="M23 47 Q32 55 41 47" stroke="#39211c" strokeWidth="3" fill="none" strokeLinecap="round" />
        </>
      )}
    </svg>
  )
}

export function HalloweenPumpkins() {
  const [header, setHeader] = useState<HTMLElement | null>(null)
  const today = new Date()
  const isHalloween = (today.getMonth() === 9 && today.getDate() >= 29) ||
    (today.getMonth() === 10 && today.getDate() <= 1)

  useEffect(() => {
    if (!isHalloween) return
    const root = document.getElementById('root')
    if (!root) return

    const updateHeader = () => {
      const current = root.querySelector<HTMLElement>('.kr-header')
      setHeader(previous => previous === current ? previous : current)
    }

    updateHeader()
    const observer = new MutationObserver(updateHeader)
    observer.observe(root, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [isHalloween])

  const pumpkins = useMemo(() => Array.from({ length: 8 }, (_, index) => {
    const style: PumpkinStyle = {
      '--x': `${random(index + 1) * 95}%`,
      '--y': `${10 + random(index + 17) * 40}%`,
      '--size': `${12 + random(index + 31) * 10}px`,
      '--duration': `${14 + random(index + 61) * 12}s`,
      '--delay': `${-random(index + 91) * 25}s`,
      '--travel': `${20 + random(index + 121) * 60}px`,
      '--opacity': `${0.22 + random(index + 181) * 0.22}`,
    }
    return { id: index, variant: index % 3, style }
  }), [])

  if (!isHalloween || !header) return null

  return createPortal(
    <div className="kr-halloween" aria-hidden="true">
      {pumpkins.map(pumpkin => (
        <span key={pumpkin.id} className="kr-halloween__pumpkin" style={pumpkin.style}>
          <span className="kr-halloween__sway"><PumpkinIcon variant={pumpkin.variant} /></span>
        </span>
      ))}
    </div>,
    header,
  )
}
